import {
    CULLFACE_NONE,
    FILTER_LINEAR,
    PIXELFORMAT_RGBA8,
    BlendState,
    Color,
    Entity,
    Layer,
    Mesh,
    MeshInstance,
    PlaneGeometry,
    Script,
    StandardMaterial,
    Texture,
    Vec3,
    BLENDEQUATION_ADD,
    BLENDMODE_ONE,
    BLENDMODE_ONE_MINUS_SRC_ALPHA,
    BLENDMODE_SRC_ALPHA
} from 'playcanvas';
import { readAnnotationMediaLayout } from './annotation-media-layout.js';

/** @import { EventHandle } from 'playcanvas' */

// clamp the vertices of the hotspot so it is never clipped by the near or far plane
const depthClamp = `
    float f = gl_Position.z / gl_Position.w;
    if (f > 1.0) {
        gl_Position.z = gl_Position.w;
    } else if (f < -1.0) {
        gl_Position.z = -gl_Position.w;
    }
`;

const depthClampWGSL = `
    let f: f32 = output.position.z / output.position.w;
    if (f > 1.0) {
        output.position.z = output.position.w;
    } else if (f < -1.0) {
        output.position.z = -output.position.w;
    }
`;

const vec = new Vec3();
const cameraPositionEpsilon = 0.01;
const desktopTooltipScaleRange = { min: 0.6, max: 1.05 };
const mobileTooltipScaleRange = { min: 0.72, max: 1.02 };
const tooltipScaleDeadband = 0.012;
const tooltipScaleSmoothing = 0.18;

/**
 * Resources managed by the AnnotationManager for each Annotation instance.
 * @typedef {object} AnnotationResources
 * @property {Entity} baseEntity - The entity for the main hotspot mesh
 * @property {Entity} overlayEntity - The entity for the overlay mesh (behind geometry)
 * @property {HTMLDivElement} hotspotDom - The clickable DOM element for the hotspot
 * @property {Texture} texture - The hotspot texture with the label
 * @property {StandardMaterial[]} materials - The materials for base and overlay
 * @property {EventHandle[]} eventHandles - Event listener handles for cleanup
 * @property {Function} domCleanup - Function to remove DOM event listeners
 * @property {{ svg: SVGSVGElement, path: SVGPathElement } | null} calloutLeader - The callout leader line
 * @property {number} calloutDirection - Stable callout side: 1 for right, -1 for left
 * @property {HTMLDivElement | null} tooltipAttachmentsDom - Persistent media container for this annotation
 */

/**
 * A manager script that handles global configuration and shared resources for all annotations
 * in a scene. Add this script to a single entity to configure annotation appearance.
 *
 * The manager listens for app-level events to automatically register annotations:
 * - `annotation:add` - fired when an Annotation script initializes
 * - `annotation:remove` - fired when an Annotation script is destroyed
 *
 * The manager handles:
 * - Global hotspot size, colors, and opacity settings (configurable in Editor)
 * - Shared rendering resources (layers, mesh)
 * - Per-annotation rendering resources (entities, materials, DOM elements)
 * - Tooltip DOM elements
 * - Hover and click interactions
 */
export class AnnotationManager extends Script {
    static scriptName = 'annotationManager';

    /** @private */
    _hotspotSize = 25;

    /** @private */
    _hotspotColor = new Color(0.42, 0.56, 1.0);

    /** @private */
    _hoverColor = new Color(0.82, 0.56, 1.0);

    /** @private */
    _opacity = 1.0;

    /** @private */
    _behindOpacity = 0.25;

    _annotationEditMode = false;
    /**
     * The size of hotspots in screen pixels.
     *
     * @attribute
     * @title Hotspot Size
     * @type {number}
     * @default 25
     */
    set hotspotSize(value) {
        if (this._hotspotSize === value) return;
        this._hotspotSize = value;
        this._updateStyleSheet();
        this._updateAllHotspotSizes();
    }

    get hotspotSize() {
        return this._hotspotSize;
    }

    /**
     * The default color of hotspots.
     *
     * @attribute
     * @title Hotspot Color
     * @type {Color}
     * @default [0.42, 0.56, 1, 1]
     */
    set hotspotColor(value) {
        if (this._hotspotColor.equals(value)) return;
        this._hotspotColor.copy(value);
        this._updateAllAnnotationColors();
    }

    get hotspotColor() {
        return this._hotspotColor;
    }

    /**
     * The color of hotspots when hovered.
     *
     * @attribute
     * @title Hotspot Hover Color
     * @type {Color}
     * @default [0.82, 0.56, 1, 1]
     */
    set hoverColor(value) {
        if (this._hoverColor.equals(value)) return;
        this._hoverColor.copy(value);
        // Update currently hovered annotation if any
        if (this._hoverAnnotation) {
            this._setAnnotationHover(this._hoverAnnotation, true);
        }
    }

    get hoverColor() {
        return this._hoverColor;
    }

    /**
     * The opacity of hotspots when visible (not occluded).
     *
     * @attribute
     * @title Hotspot Opacity
     * @type {number}
     * @range [0, 1]
     * @default 1
     */
    set opacity(value) {
        this._opacity = value;
    }

    get opacity() {
        return this._opacity;
    }

    /**
     * The opacity of hotspots when behind geometry.
     *
     * @attribute
     * @title Hotspot Behind Opacity
     * @type {number}
     * @range [0, 1]
     * @default 0.25
     */
    set behindOpacity(value) {
        this._behindOpacity = value;
    }

    get behindOpacity() {
        return this._behindOpacity;
    }

    // Shared resources

    /**
     * @type {HTMLStyleElement | null}
     * @private
     */
    _styleSheet = null;

    /**
     * @type {HTMLElement | null}
     * @private
     */
    _parentDom = null;

    /**
     * @type {Entity | null}
     * @private
     */
    _camera = null;

    /**
     * @type {HTMLDivElement | null}
     * @private
     */
    _tooltipDom = null;

    /**
     * @type {HTMLDivElement | null}
     * @private
     */
    _titleDom = null;

    /**
     * @type {HTMLDivElement | null}
     * @private
     */
    _textDom = null;

    /**
     * @type {HTMLDivElement | null}
     * @private
     */
    _attachmentsDom = null;

    /**
     * @type {Layer[]}
     * @private
     */
    _layers = [];

    /**
     * @type {Mesh | null}
     * @private
     */
    _mesh = null;

    // Per-annotation resources

    /**
     * Map of Annotation instances to their rendering resources.
     * @type {Map<Annotation, AnnotationResources>}
     * @private
     */
    _annotationResources = new Map();

    /**
     * @type {Annotation | null}
     * @private
     */
    _activeAnnotation = null;

    /**
     * Timer used to delay showing a tooltip until the camera has settled.
     * @type {number | null}
     * @private
     */
    _pendingTooltipTimer = null;

    /**
     * Annotation waiting for the delayed tooltip to be shown.
     * @type {Annotation | null}
     * @private
     */
    _pendingTooltipAnnotation = null;

    /**
     * @type {Annotation | null}
     * @private
     */
    _hoverAnnotation = null;

    /**
     * @type {boolean}
     * @private
     */
    _allTooltipsVisible = false;

    /**
     * @type {boolean}
     * @private
     */
    _calloutMode = false;

    /** @private */
    _tooltipScaleStates = new WeakMap();

    /** @private */
    _attachmentRetryCallbacks = new WeakMap();

    /**
     * Injects required CSS styles into the document.
     * @private
     */
    _injectStyles() {
        const size = this._hotspotSize;
        const css = `
            .pc-annotation {
                --pc-annotation-scale: 1;
                box-sizing: border-box;
                display: inline-flex;
                position: absolute;
                flex-direction: column;
                align-items: flex-start;
                overflow-x: hidden;
                overflow-y: auto;
                border: 1px solid rgba(255, 255, 255, 0.18);
                background: linear-gradient(145deg, rgba(28, 30, 38, 0.94), rgba(13, 14, 19, 0.9));
                color: white;
                max-height: min(29vh, 172px);
                padding: 7px 8px;
                border-radius: 14px;
                box-shadow: 0 16px 42px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.05);
                font-size: 14px;
                font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji";
                pointer-events: auto;
                max-width: min(164px, calc(100vw - 22px));
                min-width: 0;
                word-wrap: break-word;
                white-space: normal;
                width: max-content;
                opacity: 0;
                filter: blur(3px);
                transition: opacity 0.2s ease, filter 0.24s ease;
                visibility: hidden;
                transform: scale(var(--pc-annotation-scale, 1));
                transform-origin: left center;
                will-change: transform, opacity, filter;
                z-index: 22;
                white-space: pre-wrap;
                backdrop-filter: blur(18px) saturate(1.12);
                -webkit-backdrop-filter: blur(18px) saturate(1.12);
            }

            .pc-annotation::after {
                content: "";
                position: absolute;
                top: -1px;
                left: 17px;
                width: 28px;
                height: 2px;
                border-radius: 0 0 99px 99px;
                background: linear-gradient(90deg, #6f8dff, #b282ef);
                box-shadow: 0 0 12px rgba(111, 141, 255, 0.7);
                pointer-events: none;
            }

            .pc-annotation-title {
                width: 100%;
                max-width: 100%;
                margin-bottom: 4px;
                overflow-wrap: anywhere;
                font-weight: 720;
                letter-spacing: -0.01em;
                line-height: 1.35;
            }

            .pc-annotation-text {
                width: 100%;
                max-width: 100%;
                max-height: min(14vh, 72px);
                overflow-y: auto;
                overflow-wrap: anywhere;
                overscroll-behavior: contain;
                line-height: 1.58;
                scrollbar-width: thin;
            }

            .pc-annotation-attachments {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                gap: 4px;
                margin-top: 5px;
                width: max-content;
                max-width: min(140px, calc(100vw - 40px));
                max-height: min(18vh, 108px);
                overflow-x: hidden;
                overflow-y: auto;
                overscroll-behavior: contain;
            }

            .pc-annotation-attachments.is-single {
                width: 100%;
                max-width: 100%;
            }

            .pc-annotation-attachment-column {
                display: flex;
                flex: 0 1 auto;
                flex-direction: column;
                align-items: flex-start;
                min-width: 0;
                width: max-content;
                max-width: 100%;
                gap: 4px;
            }

            .pc-annotation-attachments.is-single .pc-annotation-attachment-column {
                width: 100%;
                max-width: 100%;
                flex: 0 0 auto;
            }

            .pc-annotation-attachment {
                position: relative;
                display: flex;
                flex-direction: column;
                min-width: 0;
                width: max-content;
                max-width: 100%;
                gap: 4px;
            }

            .pc-annotation-attachment.is-audio {
                box-sizing: border-box;
                width: 92px;
                padding: 0;
                border: 0;
                background: transparent;
            }

            .pc-annotation-audio-message {
                --pc-annotation-audio-progress: 0%;
                display: grid;
                box-sizing: border-box;
                width: 100%;
                height: 29px;
                margin: 0;
                padding: 0;
                grid-template-columns: 21px minmax(0, 1fr) auto;
                align-items: center;
                border: 0;
                background: transparent;
                color: rgba(255, 255, 255, 0.72);
                gap: 5px;
                cursor: pointer;
            }

            .pc-annotation-audio-toggle {
                position: relative;
                display: grid;
                width: 21px;
                height: 21px;
                place-items: center;
                border-radius: 50%;
                background: #7188ee;
                box-shadow: 0 4px 12px rgba(70, 91, 204, 0.28);
            }

            .pc-annotation-audio-toggle::before {
                width: 0;
                height: 0;
                margin-left: 2px;
                border-top: 4px solid transparent;
                border-bottom: 4px solid transparent;
                border-left: 7px solid white;
                content: "";
            }

            .pc-annotation-audio-message.is-playing .pc-annotation-audio-toggle::before {
                width: 3px;
                height: 9px;
                margin: 0 4px 0 0;
                border: 0;
                border-radius: 1px;
                background: white;
                box-shadow: 6px 0 0 white;
            }

            .pc-annotation-audio-wave {
                position: relative;
                display: flex;
                min-width: 0;
                height: 15px;
                align-items: center;
                justify-content: space-between;
                overflow: hidden;
            }

            .pc-annotation-audio-wave::after {
                content: "";
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                width: var(--pc-annotation-audio-progress);
                border-right: 1px solid rgba(141, 160, 255, 0.5);
                background: rgba(114, 137, 238, 0.08);
                pointer-events: none;
                transition: width 0.15s linear;
            }

            .pc-annotation-audio-wave i {
                width: 2px;
                height: 7px;
                flex: 0 0 auto;
                border-radius: 99px;
                background: rgba(255, 255, 255, 0.38);
            }

            .pc-annotation-audio-wave i:nth-child(3n+1) { height: 10px; }
            .pc-annotation-audio-wave i:nth-child(4n+2) { height: 15px; }
            .pc-annotation-audio-wave i:nth-child(5n+3) { height: 9px; }
            .pc-annotation-audio-message.is-playing .pc-annotation-audio-wave i {
                background: #9caeff;
                animation: pc-annotation-audio-wave 0.9s ease-in-out infinite alternate;
            }
            .pc-annotation-audio-message.is-playing .pc-annotation-audio-wave i:nth-child(2n) { animation-delay: -0.32s; }
            .pc-annotation-audio-message.is-playing .pc-annotation-audio-wave i:nth-child(3n) { animation-delay: -0.58s; }
            .pc-annotation-audio-time {
                min-width: 20px;
                color: rgba(255, 255, 255, 0.52);
                font-size: 8px;
                font-variant-numeric: tabular-nums;
                text-align: right;
            }

            .pc-annotation-attachment.is-audio > audio { display: none; }

            @keyframes pc-annotation-audio-wave {
                from { transform: scaleY(0.58); opacity: 0.58; }
                to { transform: scaleY(1.08); opacity: 1; }
            }

            .pc-annotation-attachment.is-previewable {
                cursor: zoom-in;
                outline: none;
            }

            .pc-annotation-attachment.is-previewable:focus-visible {
                border-radius: 7px;
                box-shadow: 0 0 0 2px rgba(145, 202, 255, 0.9);
            }

            .pc-annotation-preview-icon {
                position: absolute;
                z-index: 2;
                top: 6px;
                right: 6px;
                display: grid;
                width: 26px;
                height: 26px;
                place-items: center;
                border: 1px solid rgba(255, 255, 255, 0.28);
                border-radius: 50%;
                background: rgba(8, 10, 14, 0.68);
                box-shadow: 0 3px 10px rgba(0, 0, 0, 0.2);
                color: white;
                pointer-events: none;
                backdrop-filter: blur(6px);
                -webkit-backdrop-filter: blur(6px);
            }

            .pc-annotation-preview-icon svg {
                display: block;
                width: 14px;
                height: 14px;
            }

            .pc-annotation-attachment.is-video .pc-annotation-preview-icon {
                top: 50%;
                right: auto;
                left: 50%;
                width: 28px;
                height: 28px;
                transform: translate(-50%, -50%);
                border-color: rgba(255, 255, 255, 0.38);
                background: rgba(12, 12, 17, 0.7);
            }

            .pc-annotation-attachment.is-video .pc-annotation-preview-icon svg {
                width: 16px;
                height: 16px;
            }

            .pc-annotation-attachment.is-image,
            .pc-annotation-attachment.is-video {
                --pc-annotation-media-ratio: 16 / 9;
                --pc-annotation-media-width: 108px;
                width: min(var(--pc-annotation-media-width), 100%);
                aspect-ratio: var(--pc-annotation-media-ratio);
                align-items: center;
                overflow: hidden;
                border-radius: 9px;
            }

            .pc-annotation-attachments.is-single .pc-annotation-attachment.is-image,
            .pc-annotation-attachments.is-single .pc-annotation-attachment.is-video {
                width: 100%;
                max-width: 100%;
            }

            .pc-annotation-attachment.is-media-pending {
                min-height: 72px;
                aspect-ratio: auto;
            }

            .pc-annotation-attachment.is-media-portrait,
            .pc-annotation-attachment.is-media-square {
                align-self: center;
            }

            .pc-annotation-media-placeholder {
                position: relative;
                overflow: hidden;
                box-sizing: border-box;
                width: 108px;
                max-width: 100%;
                min-height: 72px;
                padding: 8px;
                border: 1px dashed rgba(255, 255, 255, 0.22);
                border-radius: 9px;
                background: rgba(255, 255, 255, 0.08);
                color: rgba(255, 255, 255, 0.72);
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 7px;
                text-align: center;
                font-size: 12px;
                line-height: 1.4;
            }

            .pc-annotation-media-placeholder::after {
                content: "";
                position: absolute;
                inset: 0;
                transform: translateX(-100%);
                background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.09), transparent);
                animation: pc-annotation-placeholder-shimmer 1.35s ease-in-out infinite;
            }

            @keyframes pc-annotation-placeholder-shimmer {
                100% {
                    transform: translateX(100%);
                }
            }

            .pc-annotation-media-placeholder.is-audio {
                min-height: 42px;
                flex-direction: row;
            }

            .pc-annotation-media-placeholder.is-video {
                min-height: 72px;
            }

            .pc-annotation-media-placeholder.is-error {
                border-color: rgba(255, 120, 117, 0.42);
                background: rgba(255, 77, 79, 0.1);
            }

            .pc-annotation-media-placeholder.is-error::after {
                display: none;
            }

            .pc-annotation-media-placeholder-icon {
                width: 30px;
                height: 30px;
                color: rgba(255, 255, 255, 0.58);
            }

            .pc-annotation-media-placeholder-icon svg {
                display: block;
                width: 100%;
                height: 100%;
            }

            .pc-annotation-media-placeholder.is-audio .pc-annotation-media-placeholder-icon {
                width: 32px;
                height: 32px;
            }

            .pc-annotation-attachment img,
            .pc-annotation-attachment video {
                display: block;
                width: 100%;
                height: 100%;
                aspect-ratio: var(--pc-annotation-media-ratio);
                object-fit: contain;
                max-width: 100%;
                border-radius: inherit;
                background: rgba(255, 255, 255, 0.08);
            }

            .pc-annotation-attachment audio {
                display: block;
                position: relative;
                z-index: 1;
                width: 100%;
                min-width: 0;
                max-width: 100%;
                height: 34px;
            }

            .pc-annotation-file-link {
                color: #91caff;
                word-break: break-all;
                text-decoration: none;
            }

            /* Add a little triangular arrow on the left edge of the tooltip */
            .pc-annotation::before {
                display: none;
            }

            .pc-annotation-leader {
                position: absolute;
                left: 0;
                top: 0;
                overflow: visible;
                pointer-events: none;
                opacity: 0;
                visibility: hidden;
                z-index: 20;
                transition: opacity 0.2s ease-out;
            }

            .pc-annotation-leader path {
                fill: none;
                stroke: rgba(255, 255, 255, 0.9);
                stroke-width: 1.5;
                stroke-linecap: round;
                stroke-linejoin: round;
                vector-effect: non-scaling-stroke;
                filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.42));
                stroke-dasharray: 1;
                stroke-dashoffset: 1;
                transition: stroke-dashoffset 0.34s cubic-bezier(0.22, 0.78, 0.24, 1);
            }

            .pc-annotation-leader.is-visible path {
                stroke-dashoffset: 0;
            }

            .pc-annotation-hotspot {
                display: none;
                position: absolute;
                width: ${size + 5}px;
                height: ${size + 5}px;
                opacity: 1;
                cursor: pointer;
                transform: translate(-50%, -50%);
                pointer-events: auto;
                z-index: 21;
            }

            .pc-annotation-hotspot::before,
            .pc-annotation-hotspot::after {
                content: "";
                position: absolute;
                border-radius: 50%;
                pointer-events: none;
            }

            .pc-annotation-hotspot::before {
                inset: 1px;
                border: 1px solid rgba(121, 146, 255, 0.72);
                box-shadow: 0 0 0 3px rgba(73, 104, 255, 0.1), 0 0 14px rgba(73, 104, 255, 0.28);
                animation: pc-annotation-hotspot-breathe 2.6s ease-in-out infinite;
            }

            .pc-annotation-hotspot::after {
                inset: -4px;
                border: 1px solid rgba(141, 116, 235, 0);
                transform: scale(0.82);
                transition: border-color 0.22s ease, transform 0.32s cubic-bezier(0.2, 0.8, 0.24, 1);
            }

            .pc-annotation-hotspot:hover::after,
            .pc-annotation-hotspot.is-active::after,
            .pc-annotation-hotspot.is-expanded::after {
                border-color: rgba(151, 125, 242, 0.58);
                transform: scale(1.08);
            }

            .pc-annotation-hotspot.is-active::before {
                border-color: rgba(196, 180, 255, 0.94);
                box-shadow: 0 0 0 4px rgba(110, 94, 233, 0.15), 0 0 20px rgba(112, 126, 255, 0.44);
            }

            @keyframes pc-annotation-hotspot-breathe {
                0%, 100% { transform: scale(0.92); opacity: 0.58; }
                50% { transform: scale(1.08); opacity: 1; }
            }

            @media (prefers-reduced-motion: reduce) {
                .pc-annotation,
                .pc-annotation-leader path,
                .pc-annotation-hotspot::before,
                .pc-annotation-hotspot::after,
                .pc-annotation-audio-wave i {
                    animation: none !important;
                    transition: none !important;
                }
            }
        `;

        const style = document.createElement('style');
        style.textContent = css;
        document.head.appendChild(style);
        this._styleSheet = style;
    }

    /**
     * Updates the stylesheet when hotspot size changes.
     * @private
     */
    _updateStyleSheet() {
        if (this._styleSheet) {
            this._styleSheet.remove();
            this._styleSheet = null;
        }
        this._injectStyles();
    }

    /**
     * Returns per-annotation size multiplier.
     * @param {Annotation} annotation - The annotation
     * @returns {number}
     * @private
     */
    _getAnnotationSizeScale(annotation) {
        const size = Number(annotation?.size);
        return Number.isFinite(size) && size > 0 ? size : 1;
    }

    /**
     * Updates hotspot DOM size for a single annotation.
     * @param {Annotation} annotation - The annotation
     * @private
     */
    _applyAnnotationHotspotSize(annotation) {
        const resources = this._annotationResources.get(annotation);
        if (!resources) return;
        const scale = this._getAnnotationSizeScale(annotation);
        const size = this._hotspotSize * scale;
        resources.hotspotDom.style.width = `${size + 5}px`;
        resources.hotspotDom.style.height = `${size + 5}px`;
    }

    /**
     * Updates hotspot DOM sizes for all annotations.
     * @private
     */
    _updateAllHotspotSizes() {
        for (const [annotation] of this._annotationResources) {
            this._applyAnnotationHotspotSize(annotation);
        }
    }

    /**
     * Applies tooltip text style for a single annotation.
     * @param {Annotation} annotation - The annotation
     * @private
     */
    _applyAnnotationTextStyle(annotation, elements = null) {
        const titleDom = elements?.titleDom || this._titleDom;
        const textDom = elements?.textDom || this._textDom;
        if (!titleDom || !textDom) return;
        const titleColor = String(annotation?.titleColor || '').trim();
        const textColor = String(annotation?.textColor || '').trim();
        const textSize = Number(annotation?.textSize);
        const normalizedTextSize = Number.isFinite(textSize) ? Math.min(32, Math.max(10, Math.round(textSize))) : 14;
        titleDom.style.color = /^#[0-9a-f]{6}$/i.test(titleColor) ? titleColor : '#ffffff';
        titleDom.style.fontSize = `${normalizedTextSize + 2}px`;
        textDom.style.color = /^#[0-9a-f]{6}$/i.test(textColor) ? textColor : '#ffffff';
        textDom.style.fontSize = `${normalizedTextSize}px`;
    }

    /**
     * Updates all annotation materials when hotspot color changes.
     * @private
     */
    _updateAllAnnotationColors() {
        for (const [annotation, resources] of this._annotationResources) {
            // Only update non-hovered annotations
            if (annotation !== this._hoverAnnotation) {
                resources.materials.forEach((material) => {
                    material.emissive.copy(this._hotspotColor);
                    material.update();
                });
            }
        }
    }

    /**
     * Returns the DOM parent that stays visible when the viewer enters fullscreen.
     * @returns {HTMLElement}
     * @private
     */
    _resolveParentDom() {
        const canvas = this.app?.graphicsDevice?.canvas;
        const parent = canvas?.closest?.('.render-task-container')
            || canvas?.parentElement
            || document.body;
        if (parent !== document.body && window.getComputedStyle(parent).position === 'static') {
            parent.style.position = 'relative';
        }
        return parent;
    }

    /**
     * Returns the untransformed local bounds for the DOM parent.
     * @returns {{ left: number, top: number, right: number, bottom: number, width: number, height: number }}
     * @private
     */
    _getParentBounds() {
        if (!this._parentDom || this._parentDom === document.body) {
            return {
                left: 0,
                top: 0,
                right: window.innerWidth,
                bottom: window.innerHeight,
                width: window.innerWidth,
                height: window.innerHeight
            };
        }
        const rect = this._parentDom.getBoundingClientRect();
        const width = this._parentDom.clientWidth || rect.width;
        const height = this._parentDom.clientHeight || rect.height;
        return {
            left: 0,
            top: 0,
            right: width,
            bottom: height,
            width,
            height
        };
    }

    /**
     * Creates a circular hotspot texture.
     * @param {string} label - Label text to draw on the hotspot
     * @param {number} [size] - The texture size (should be power of 2)
     * @param {number} [borderWidth] - The border width in pixels
     * @returns {Texture} The hotspot texture
     * @private
     */
    _createHotspotTexture(label, size = 64, borderWidth = 6) {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');

        // First clear with stroke color at zero alpha
        ctx.fillStyle = 'white';
        ctx.globalAlpha = 0;
        ctx.fillRect(0, 0, size, size);
        ctx.globalAlpha = 1.0;

        // Draw dark circle with light border
        const centerX = size / 2;
        const centerY = size / 2;
        const radius = (size / 2) - 4;

        // Draw main circle
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.fillStyle = 'black';
        ctx.fill();

        // Draw border
        ctx.beginPath();
        ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
        ctx.lineWidth = borderWidth;
        ctx.strokeStyle = 'white';
        ctx.stroke();

        // Draw text
        ctx.font = 'bold 32px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = 'white';
        ctx.fillText(label, Math.floor(canvas.width / 2), Math.floor(canvas.height / 2) + 1);

        // get pixel data
        const imageData = ctx.getImageData(0, 0, size, size);
        const data = imageData.data;

        // set the color channel of semitransparent pixels to white so the blending at
        // the edges is correct
        for (let i = 0; i < data.length; i += 4) {
            const a = data[i + 3];
            if (a < 255) {
                data[i] = 255;
                data[i + 1] = 255;
                data[i + 2] = 255;
            }
        }

        return new Texture(this.app.graphicsDevice, {
            width: size,
            height: size,
            format: PIXELFORMAT_RGBA8,
            magFilter: FILTER_LINEAR,
            minFilter: FILTER_LINEAR,
            mipmaps: false,
            levels: [new Uint8Array(data.buffer)]
        });
    }

    /**
     * Creates the compact anchor used by the callout presentation.
     * @param {number} [size] - The texture size (should be power of 2)
     * @returns {Texture} The callout anchor texture
     * @private
     */
    _createCalloutDotTexture(size = 64) {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        const center = size / 2;

        ctx.clearRect(0, 0, size, size);
        ctx.beginPath();
        ctx.arc(center, center, 15, 0, Math.PI * 2);
        ctx.fillStyle = 'white';
        ctx.fill();

        ctx.beginPath();
        ctx.arc(center, center, 7, 0, Math.PI * 2);
        ctx.fillStyle = 'black';
        ctx.fill();

        const imageData = ctx.getImageData(0, 0, size, size);
        const data = imageData.data;
        for (let i = 0; i < data.length; i += 4) {
            if (data[i + 3] < 255) {
                data[i] = 255;
                data[i + 1] = 255;
                data[i + 2] = 255;
            }
        }

        return new Texture(this.app.graphicsDevice, {
            width: size,
            height: size,
            format: PIXELFORMAT_RGBA8,
            magFilter: FILTER_LINEAR,
            minFilter: FILTER_LINEAR,
            mipmaps: false,
            levels: [new Uint8Array(data.buffer)]
        });
    }

    _createAnnotationTexture(annotation) {
        return this._calloutMode
            ? this._createCalloutDotTexture()
            : this._createHotspotTexture(annotation?.label || '');
    }

    _replaceAnnotationTexture(annotation, resources) {
        if (!resources) return;
        const previousTexture = resources.texture;
        resources.texture = this._createAnnotationTexture(annotation);
        resources.materials.forEach((material) => {
            material.emissiveMap = resources.texture;
            material.opacityMap = resources.texture;
            material.update();
        });
        previousTexture?.destroy();
    }

    setCalloutMode(active) {
        const next = Boolean(active);
        if (this._calloutMode === next) return this._calloutMode;
        this._calloutMode = next;
        this._tooltipDom?.classList.toggle('pc-annotation-callout', next);
        for (const [annotation, resources] of this._annotationResources) {
            resources.calloutDirection = 0;
            resources.expandedTooltip?.tooltipDom.classList.toggle('pc-annotation-callout', next);
            if (!next) this._hideCalloutLeader(resources);
            this._replaceAnnotationTexture(annotation, resources);
        }
        return this._calloutMode;
    }

    /**
     * Creates a material for hotspot rendering.
     * @param {Texture} texture - The texture to use for emissive and opacity
     * @param {object} [options] - Material options
     * @param {number} [options.opacity] - Base opacity multiplier
     * @param {boolean} [options.depthTest] - Whether to perform depth testing
     * @param {boolean} [options.depthWrite] - Whether to write to depth buffer
     * @returns {StandardMaterial} The configured material
     * @private
     */
    _createHotspotMaterial(texture, { opacity = 1, depthTest = true, depthWrite = true } = {}) {
        const material = new StandardMaterial();

        material.diffuse = Color.BLACK;
        material.emissive.copy(this._hotspotColor);
        material.emissiveMap = texture;
        material.opacityMap = texture;

        material.opacity = opacity;
        material.alphaTest = 0.01;
        material.blendState = new BlendState(
            true,
            BLENDEQUATION_ADD, BLENDMODE_SRC_ALPHA, BLENDMODE_ONE_MINUS_SRC_ALPHA,
            BLENDEQUATION_ADD, BLENDMODE_ONE, BLENDMODE_ONE
        );

        material.depthTest = depthTest;
        material.depthWrite = depthWrite;
        material.cull = CULLFACE_NONE;
        material.useLighting = false;

        material.shaderChunks.glsl.add({
            'litUserMainEndVS': depthClamp
        });

        material.shaderChunks.wgsl.add({
            'litUserMainEndVS': depthClampWGSL
        });

        material.update();
        return material;
    }

    /**
     * Sets the hover state visual for an annotation.
     * @param {Annotation} annotation - The annotation
     * @param {boolean} hover - Whether hovered
     * @private
     */
    _setAnnotationHover(annotation, hover) {
        const resources = this._annotationResources.get(annotation);
        if (!resources) return;

        resources.materials.forEach((material) => {
            material.emissive.copy(hover ? this._hoverColor : this._hotspotColor);
            material.update();
        });
        annotation.fire('hover', hover);
    }

    /**
     * Shows the tooltip for an annotation.
     * @param {Annotation} annotation - The annotation to show tooltip for
     * @private
     */
    _showTooltip(annotation) {
        this._activeAnnotation = annotation;
        this._annotationResources.get(annotation)?.hotspotDom?.classList.add('is-active');
        this._titleDom.textContent = annotation.title;
        this._textDom.textContent = annotation.text;
        this._applyAnnotationTextStyle(annotation);
        this._renderAttachments(annotation);
        this._retryFailedAttachments(this._attachmentsDom);
        this._applyTooltipScale(this._tooltipDom, annotation);
        this._showTooltipDom(this._tooltipDom);
        this._playTooltipMedia(this._attachmentsDom);
        annotation.fire('show', annotation);
    }

    _createAttachmentsDom() {
        const attachmentsDom = document.createElement('div');
        attachmentsDom.className = 'pc-annotation-attachments';
        return attachmentsDom;
    }

    _createTooltipElements(extraClass = '', attachmentsDom = null) {
        const tooltipDom = document.createElement('div');
        tooltipDom.className = `pc-annotation${extraClass ? ` ${extraClass}` : ''}`;

        const titleDom = document.createElement('div');
        titleDom.className = 'pc-annotation-title';
        tooltipDom.appendChild(titleDom);

        const textDom = document.createElement('div');
        textDom.className = 'pc-annotation-text';
        tooltipDom.appendChild(textDom);

        const resolvedAttachmentsDom = attachmentsDom || this._createAttachmentsDom();
        tooltipDom.appendChild(resolvedAttachmentsDom);

        tooltipDom.addEventListener('pointerdown', event => event.stopPropagation());
        this._parentDom.appendChild(tooltipDom);

        return {
            tooltipDom,
            titleDom,
            textDom,
            attachmentsDom: resolvedAttachmentsDom
        };
    }

    _populateTooltipElements(annotation, elements) {
        if (!annotation || !elements) return;
        elements.titleDom.textContent = annotation.title;
        elements.textDom.textContent = annotation.text;
        this._applyAnnotationTextStyle(annotation, elements);
    }

    _ensureCalloutLeader(resources) {
        if (!resources.calloutLeader) {
            const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
            svg.classList.add('pc-annotation-leader');
            svg.setAttribute('aria-hidden', 'true');
            const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            path.setAttribute('pathLength', '1');
            svg.appendChild(path);
            this._parentDom.appendChild(svg);
            resources.calloutLeader = { svg, path };
        }
        return resources.calloutLeader;
    }

    _ensureExpandedTooltip(annotation, resources) {
        const attachmentsDom = this._ensureAnnotationAttachmentsDom(annotation, resources);
        if (!resources.expandedTooltip) {
            this._ensureCalloutLeader(resources);
            resources.expandedTooltip = this._createTooltipElements(
                'pc-annotation-expanded',
                attachmentsDom
            );
            resources.expandedTooltip.tooltipDom.classList.toggle(
                'pc-annotation-callout',
                this._calloutMode
            );
            this._populateTooltipElements(annotation, resources.expandedTooltip);
        } else if (attachmentsDom.parentNode !== resources.expandedTooltip.tooltipDom) {
            resources.expandedTooltip.tooltipDom.appendChild(attachmentsDom);
        }
        resources.expandedTooltip.attachmentsDom = attachmentsDom;
        return resources.expandedTooltip;
    }

    _hideCalloutLeader(resources) {
        const svg = resources?.calloutLeader?.svg;
        if (!svg) return;
        svg.classList.remove('is-visible');
        svg.style.opacity = '0';
        svg.style.visibility = 'hidden';
    }

    _normalizeAttachmentPosition(value) {
        const position = Number(value);
        if (!Number.isInteger(position)) return null;
        return position >= 0 && position < 3 ? position : null;
    }

    _inferAttachmentKind(kindValue, mimeValue, nameValue = '', sourceValue = '') {
        const explicitKind = String(kindValue || '').toLowerCase();
        if (['image', 'video', 'audio'].includes(explicitKind)) return explicitKind;

        const mime = String(mimeValue || '').toLowerCase();
        if (mime.startsWith('image/')) return 'image';
        if (mime.startsWith('video/')) return 'video';
        if (mime.startsWith('audio/')) return 'audio';

        const source = `${nameValue || ''} ${sourceValue || ''}`.split('?')[0].split('#')[0].toLowerCase();
        if (/\.(png|jpe?g|gif|webp|bmp|svg|avif|heic|heif)$/.test(source)) return 'image';
        if (/\.(mp4|webm|mov|m4v|avi|mkv|ogv)$/.test(source)) return 'video';
        if (/\.(mp3|wav|m4a|aac|ogg|oga|flac|opus|weba|amr)$/.test(source)) return 'audio';

        return 'file';
    }

    _normalizeAttachmentLayout(attachments) {
        const sorted = attachments
            .filter(item => item && this._normalizeAttachmentPosition(item.position) !== null)
            .sort((a, b) => a.position - b.position)
            .slice(0, 3);

        if (sorted.length <= 1) {
            return sorted.map(item => ({
                ...item,
                position: 0
            }));
        }

        if (sorted.length === 2) {
            const bottomAttachment = sorted.find(item => item.position === 2);
            if (bottomAttachment) {
                const topAttachment = sorted.find(item => item !== bottomAttachment);
                return [
                    { ...topAttachment, position: 0 },
                    { ...bottomAttachment, position: 2 }
                ].sort((a, b) => a.position - b.position);
            }

            return sorted.map((item, index) => ({
                ...item,
                position: index
            }));
        }

        return sorted.map((item, index) => ({
            ...item,
            position: index
        }));
    }

    _normalizeAttachments(attachments) {
        if (!Array.isArray(attachments)) return [];
        const usedPositions = new Set();
        const positioned = [];
        const pending = [];

        attachments
            .filter(item => item && (
                item.url || item.src || item.dataUrl || item.fileUrl || item.file_url ||
                item.signedUrl || item.signed_url || item.downloadUrl || item.download_url
            ))
            .map((item, index) => {
                const mime = String(item.mime || item.type || item.contentType || item.content_type || '').toLowerCase();
                const source = item.url || item.src || item.dataUrl || item.fileUrl || item.file_url ||
                    item.signedUrl || item.signed_url || item.downloadUrl || item.download_url;
                const name = String(item.name || item.fileName || item.file_name || 'attachment');
                const kind = this._inferAttachmentKind(item.kind, mime, name, source);
                return {
                    ...item,
                    name,
                    kind,
                    mime,
                    position: this._normalizeAttachmentPosition(item.position ?? item.slot),
                    sourceIndex: index,
                    url: source
                };
            })
            .forEach(item => {
                const { sourceIndex, ...attachment } = item;
                if (attachment.position !== null && !usedPositions.has(attachment.position)) {
                    usedPositions.add(attachment.position);
                    positioned.push(attachment);
                    return;
                }
                pending.push(attachment);
            });

        pending.forEach(attachment => {
            const nextPosition = [0, 1, 2].find(position => !usedPositions.has(position));
            if (typeof nextPosition === 'undefined') return;
            usedPositions.add(nextPosition);
            positioned.push({
                ...attachment,
                position: nextPosition
            });
        });

        return this._normalizeAttachmentLayout(positioned);
    }

    _ensureAnnotationAttachmentsDom(annotation, resources) {
        if (!resources.tooltipAttachmentsDom) {
            resources.tooltipAttachmentsDom = this._createAttachmentsDom();
            this._renderAttachmentsInto(annotation, resources.tooltipAttachmentsDom);
        }
        return resources.tooltipAttachmentsDom;
    }

    _renderAttachments(annotation) {
        const resources = this._annotationResources.get(annotation);
        if (!resources || !this._tooltipDom) return;

        const attachmentsDom = this._ensureAnnotationAttachmentsDom(annotation, resources);

        if (this._attachmentsDom !== attachmentsDom || attachmentsDom.parentNode !== this._tooltipDom) {
            this._stopTooltipMedia(this._attachmentsDom);
            if (this._attachmentsDom?.parentNode === this._tooltipDom) {
                this._attachmentsDom.replaceWith(attachmentsDom);
            } else {
                this._tooltipDom.appendChild(attachmentsDom);
            }
            this._attachmentsDom = attachmentsDom;
        }
    }

    _applyAttachmentMediaLayout(media, wrap) {
        const layout = readAnnotationMediaLayout(media);
        if (!layout || !wrap) return;
        wrap.classList.remove(
            'is-media-pending',
            'is-media-portrait',
            'is-media-square',
            'is-media-landscape'
        );
        wrap.classList.add(`is-media-${layout.orientation}`);
        wrap.style.setProperty('--pc-annotation-media-ratio', String(layout.ratio));
        const mediaWidth = Math.min(108, layout.ratio * 150);
        wrap.style.setProperty('--pc-annotation-media-width', `${mediaWidth}px`);
    }

    _playMediaElement(media) {
        if (!media) return;
        media.loop = true;
        const playResult = media.play?.();
        if (playResult?.catch) {
            playResult.catch(error => {
                console.debug('annotation media autoplay blocked:', error);
            });
        }
    }

    _playTooltipMedia(container) {
        requestAnimationFrame(() => {
            container
                ?.querySelectorAll?.('video')
                ?.forEach(media => this._playMediaElement(media));
        });
    }

    _stopTooltipMedia(container) {
        container
            ?.querySelectorAll?.('video, audio')
            ?.forEach(media => {
                media.pause?.();
                media.currentTime = 0;
            });
        container?.querySelectorAll?.('.pc-annotation-audio-message')?.forEach(button => {
            button.classList.remove('is-playing');
            button.style.setProperty('--pc-annotation-audio-progress', '0%');
        });
    }

    _retryFailedAttachments(container) {
        container
            ?.querySelectorAll?.('.pc-annotation-attachment.is-load-error')
            ?.forEach(wrap => this._attachmentRetryCallbacks.get(wrap)?.());
    }

    _renderAttachmentsInto(annotation, attachmentsDom) {
        if (!attachmentsDom) return;
        attachmentsDom.replaceChildren();
        const attachments = this._normalizeAttachments(annotation?.attachments);
        attachmentsDom.classList.toggle('is-single', attachments.length === 1);
        attachmentsDom.classList.toggle('has-multiple', attachments.length > 1);
        attachmentsDom.style.display = attachments.length ? 'flex' : 'none';
        attachmentsDom.style.removeProperty('width');
        attachmentsDom.style.removeProperty('grid-template-columns');
        attachmentsDom.style.maxWidth = 'min(140px, calc(100vw - 40px))';

        const columns = [0, 1].map(() => {
            const column = document.createElement('div');
            column.className = 'pc-annotation-attachment-column';
            return column;
        });

        const formatAudioTime = value => {
            const seconds = Math.max(0, Math.floor(Number(value) || 0));
            return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
        };

        attachments.forEach(item => {
            const wrap = document.createElement('div');
            wrap.className = `pc-annotation-attachment is-${item.kind}`;
            if (item.kind === 'image' || item.kind === 'video') {
                wrap.classList.add('is-media-pending');
                wrap.classList.add('is-previewable');
                wrap.tabIndex = 0;
                wrap.setAttribute('role', 'button');
                wrap.setAttribute('aria-label', `预览${item.kind === 'video' ? '视频' : '图片'} ${item.name || ''}`.trim());

                const openPreview = (event) => {
                    if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
                    event.preventDefault();
                    event.stopPropagation();
                    const media = wrap.querySelector(item.kind === 'video' ? 'video' : 'img');
                    if (item.kind === 'video') media?.pause?.();
                    this.app.fire('annotation:attachmentPreview', {
                        attachment: { ...item },
                        kind: item.kind,
                        mediaLayout: readAnnotationMediaLayout(media)
                    });
                };
                wrap.addEventListener('click', openPreview);
                wrap.addEventListener('keydown', openPreview);
            }

            const createMediaPlaceholder = (kind) => {
                const mediaLabels = {
                    image: {
                        loading: 'Loading image',
                        error: 'Image unavailable',
                        icon: '<svg viewBox="0 0 48 48" fill="none" aria-hidden="true"><rect x="5" y="7" width="38" height="34" rx="5" stroke="currentColor" stroke-width="2.5"/><circle cx="17" cy="18" r="4" fill="currentColor"/><path d="m9 36 10-10 7 7 5-5 8 8" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>'
                    },
                    video: {
                        loading: 'Loading video',
                        error: 'Video unavailable',
                        icon: '<svg viewBox="0 0 48 48" fill="none" aria-hidden="true"><rect x="5" y="8" width="38" height="32" rx="6" stroke="currentColor" stroke-width="2.5"/><path d="m21 18 11 6-11 6V18Z" fill="currentColor"/></svg>'
                    },
                    audio: {
                        loading: 'Loading audio',
                        error: 'Audio unavailable',
                        icon: '<svg viewBox="0 0 48 48" fill="none" aria-hidden="true"><path d="M14 20v8m7-13v18m7-23v28m7-20v12" stroke="currentColor" stroke-width="3" stroke-linecap="round"/></svg>'
                    }
                };
                const labels = mediaLabels[kind];
                const placeholder = document.createElement('div');
                placeholder.className = `pc-annotation-media-placeholder is-${kind}`;
                placeholder.setAttribute('role', 'status');
                placeholder.setAttribute('aria-label', labels.loading);

                const icon = document.createElement('span');
                icon.className = 'pc-annotation-media-placeholder-icon';
                icon.setAttribute('aria-hidden', 'true');
                icon.innerHTML = labels.icon;

                placeholder.appendChild(icon);
                return {
                    element: placeholder,
                    showLoading() {
                        placeholder.classList.remove('is-error');
                        placeholder.setAttribute('aria-label', labels.loading);
                    },
                    showError() {
                        placeholder.classList.add('is-error');
                        placeholder.setAttribute('aria-label', labels.error);
                    }
                };
            };

            const appendMediaWithPlaceholder = (media, kind, loadedEvent) => {
                const placeholder = createMediaPlaceholder(kind);
                wrap.classList.remove('is-load-error');
                placeholder.showLoading();
                media.style.display = 'none';
                const handleLoaded = () => {
                    media.removeEventListener('error', handleError);
                    wrap.classList.remove('is-load-error');
                    this._applyAttachmentMediaLayout(media, wrap);
                    placeholder.element.remove();
                    media.style.removeProperty('display');
                };
                const handleError = () => {
                    media.removeEventListener(loadedEvent, handleLoaded);
                    wrap.classList.add('is-load-error');
                    placeholder.showError();
                };
                media.addEventListener(loadedEvent, handleLoaded, { once: true });
                media.addEventListener('error', handleError, { once: true });
                wrap.append(placeholder.element, media);
            };

            const renderAttachment = () => {
                wrap.replaceChildren();
                wrap.classList.remove('is-load-error');

                if (item.kind === 'image') {
                    const img = document.createElement('img');
                    img.alt = item.name;
                    img.decoding = 'async';
                    img.crossOrigin = 'anonymous';
                    appendMediaWithPlaceholder(img, 'image', 'load');
                    img.src = item.url;
                } else if (item.kind === 'video') {
                    const video = document.createElement('video');
                    video.crossOrigin = 'anonymous';
                    video.controls = false;
                    video.autoplay = true;
                    video.loop = true;
                    video.muted = true;
                    video.playsInline = true;
                    video.preload = 'auto';
                    video.addEventListener('loadedmetadata', () => this._playMediaElement(video));
                    video.addEventListener('loadeddata', () => this._playMediaElement(video));
                    appendMediaWithPlaceholder(video, 'video', 'loadeddata');
                    video.src = item.url;
                } else if (item.kind === 'audio') {
                    const audio = document.createElement('audio');
                    audio.crossOrigin = 'anonymous';
                    audio.controls = false;
                    audio.autoplay = false;
                    audio.loop = false;
                    audio.preload = 'metadata';

                    const placeholder = createMediaPlaceholder('audio');
                    const message = document.createElement('button');
                    message.type = 'button';
                    message.className = 'pc-annotation-audio-message';
                    message.style.display = 'none';
                    message.setAttribute('aria-label', `播放录音 ${item.name || ''}`.trim());

                    const toggle = document.createElement('span');
                    toggle.className = 'pc-annotation-audio-toggle';
                    toggle.setAttribute('aria-hidden', 'true');
                    const wave = document.createElement('span');
                    wave.className = 'pc-annotation-audio-wave';
                    wave.setAttribute('aria-hidden', 'true');
                    for (let index = 0; index < 14; index += 1) wave.appendChild(document.createElement('i'));
                    const time = document.createElement('time');
                    time.className = 'pc-annotation-audio-time';
                    time.textContent = '00:00';
                    message.append(toggle, wave, time);

                    const updateAudioUi = () => {
                        const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
                        const current = Number.isFinite(audio.currentTime) ? audio.currentTime : 0;
                        const progress = duration > 0 ? Math.min(1, current / duration) : 0;
                        message.classList.toggle('is-playing', !audio.paused && !audio.ended);
                        message.style.setProperty('--pc-annotation-audio-progress', `${Math.round(progress * 100)}%`);
                        message.setAttribute('aria-label', `${audio.paused ? '播放' : '暂停'}录音 ${item.name || ''}`.trim());
                        time.textContent = formatAudioTime(!audio.paused && current > 0 ? current : duration);
                    };

                    audio.addEventListener('loadedmetadata', () => {
                        wrap.classList.remove('is-load-error');
                        placeholder.element.remove();
                        message.style.removeProperty('display');
                        updateAudioUi();
                    }, { once: true });
                    audio.addEventListener('timeupdate', updateAudioUi);
                    audio.addEventListener('play', updateAudioUi);
                    audio.addEventListener('pause', updateAudioUi);
                    audio.addEventListener('ended', () => {
                        audio.currentTime = 0;
                        updateAudioUi();
                    });
                    audio.addEventListener('error', () => {
                        wrap.classList.add('is-load-error');
                        placeholder.showError();
                    }, { once: true });
                    message.addEventListener('click', event => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (!audio.paused) {
                            audio.pause();
                            return;
                        }
                        this._parentDom?.querySelectorAll?.('.pc-annotation-attachment audio')?.forEach(other => {
                            if (other !== audio) other.pause?.();
                        });
                        if (audio.ended) audio.currentTime = 0;
                        const playResult = audio.play?.();
                        playResult?.catch?.(() => updateAudioUi());
                    });

                    wrap.append(placeholder.element, message, audio);
                    audio.src = item.url;
                } else {
                    const link = document.createElement('a');
                    link.className = 'pc-annotation-file-link';
                    link.href = item.url;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    link.textContent = item.name;
                    wrap.appendChild(link);
                }

                if (item.kind === 'image' || item.kind === 'video') {
                    const previewIcon = document.createElement('span');
                    previewIcon.className = 'pc-annotation-preview-icon';
                    previewIcon.setAttribute('aria-hidden', 'true');
                    previewIcon.innerHTML = item.kind === 'video'
                        ? '<svg viewBox="0 0 24 24" fill="none"><path d="m9 7 8 5-8 5V7Z" fill="currentColor"/></svg>'
                        : '<svg viewBox="0 0 24 24" fill="none"><circle cx="10.5" cy="10.5" r="5.75" stroke="currentColor" stroke-width="1.8"/><path d="m15 15 4 4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';
                    wrap.appendChild(previewIcon);
                }
            };

            if (['image', 'video', 'audio'].includes(item.kind)) {
                this._attachmentRetryCallbacks.set(wrap, renderAttachment);
            }
            renderAttachment();

            columns[item.position % 2].appendChild(wrap);
        });

        columns.forEach(column => {
            if (column.childElementCount > 0) {
                attachmentsDom.appendChild(column);
            }
        });

        this._playTooltipMedia(attachmentsDom);
    }

    /**
     * Hides the tooltip.
     * @param {Annotation} annotation - The annotation that was active
     * @private
     */
    _hideTooltip(annotation) {
        this._clearPendingTooltip();
        this._hideCalloutLeader(this._annotationResources.get(annotation));
        this._annotationResources.get(annotation)?.hotspotDom?.classList.remove('is-active');
        this._activeAnnotation = null;
        this._stopTooltipMedia(this._attachmentsDom);
        this._tooltipDom.style.opacity = '0';

        // Wait for fade out before hiding
        setTimeout(() => {
            if (this._tooltipDom && this._activeAnnotation === null) {
                this._tooltipDom.style.visibility = 'hidden';
            }
            annotation.fire('hide');
        }, 200);
    }

    /**
     * Cancels a delayed tooltip activation, if any.
     * @private
     */
    _clearPendingTooltip() {
        if (this._pendingTooltipTimer !== null) {
            clearTimeout(this._pendingTooltipTimer);
            this._pendingTooltipTimer = null;
        }
        this._pendingTooltipAnnotation = null;
    }

    /**
     * Checks whether the camera is already close enough to the annotation's
     * saved camera position that no transition wait is needed.
     * @param {Annotation} annotation - The annotation to inspect
     * @returns {boolean}
     * @private
     */
    _isCameraNearAnnotationPosition(annotation) {
        const target = annotation?.cameraPose?.position;
        const current = this._camera?.getPosition?.();
        if (!target || !current) return false;

        const targetX = Number(target.x);
        const targetY = Number(target.y);
        const targetZ = Number(target.z);
        if (![targetX, targetY, targetZ].every(Number.isFinite)) return false;

        const dx = current.x - targetX;
        const dy = current.y - targetY;
        const dz = current.z - targetZ;
        return dx * dx + dy * dy + dz * dz <= cameraPositionEpsilon ** 2;
    }

    /**
     * Activates an annotation as if the hotspot was clicked.
     * @param {Annotation} annotation - The annotation to activate
     * @param {Event} [event] - Optional source event
     * @private
     */
    _activateAnnotation(annotation, event) {
        if (!annotation || annotation.label === '0') return;

        this.app.fire('annotation:click', annotation, event);
        if (this._annotationEditMode) return;
        if (this._allTooltipsVisible) return;

        // Clicking the same hotspot toggles its tooltip. The document-level
        // dismiss handler deliberately ignores hotspot pointerdown events so
        // this comparison is still valid when the click event arrives.
        if (this._activeAnnotation === annotation || this._pendingTooltipAnnotation === annotation) {
            this._clearPendingTooltip();
            if (this._activeAnnotation === annotation) {
                this._hideTooltip(annotation);
            }
            return;
        }

        this._clearPendingTooltip();

        // Hide current tooltip immediately (if any)
        if (this._activeAnnotation) {
            const activeResources = this._annotationResources.get(this._activeAnnotation);
            this._hideCalloutLeader(activeResources);
            activeResources?.hotspotDom?.classList.remove('is-active');
            this._stopTooltipMedia(this._attachmentsDom);
            this._activeAnnotation = null;
            this._tooltipDom.style.opacity = '0';
            this._tooltipDom.style.visibility = 'hidden';
        }

        // If the camera is already at the saved pose, show immediately;
        // otherwise wait for the camera transition to settle.
        const tooltipDelay = this._isCameraNearAnnotationPosition(annotation) ? 0 : 500;
        if (tooltipDelay === 0) {
            this._showTooltip(annotation);
            return;
        }

        this._pendingTooltipAnnotation = annotation;
        this._pendingTooltipTimer = setTimeout(() => {
            this._pendingTooltipTimer = null;
            this._pendingTooltipAnnotation = null;
            if (!this._annotationResources.has(annotation)) return;
            this._showTooltip(annotation);
        }, 500);
    }

    /**
     * Public API: focus/open a given annotation.
     * @param {Annotation} annotation - The annotation script instance
     * @returns {boolean} True if handled, false otherwise
     */
    focusAnnotation(annotation) {
        if (!annotation || !this._annotationResources.has(annotation)) return false;
        this._activateAnnotation(annotation);
        return true;
    }

    showAllTooltips() {
        this._clearPendingTooltip();
        this._allTooltipsVisible = true;
        if (this._activeAnnotation) {
            const activeResources = this._annotationResources.get(this._activeAnnotation);
            this._hideCalloutLeader(activeResources);
            activeResources?.hotspotDom?.classList.remove('is-active');
            this._stopTooltipMedia(this._attachmentsDom);
            this._activeAnnotation = null;
            this._tooltipDom.style.opacity = '0';
            this._tooltipDom.style.visibility = 'hidden';
        }
        for (const [annotation, resources] of this._annotationResources) {
            if (!annotation || annotation.label === '0' || !annotation.entity?.enabled) continue;
            resources.hotspotDom?.classList.add('is-expanded');
            const elements = this._ensureExpandedTooltip(annotation, resources);
            this._retryFailedAttachments(elements.attachmentsDom);
            this._applyTooltipScale(elements.tooltipDom, annotation);
            this._showTooltipDom(elements.tooltipDom);
            this._playTooltipMedia(elements.attachmentsDom);
        }
    }

    hideAllTooltips() {
        this._clearPendingTooltip();
        this._allTooltipsVisible = false;
        for (const [, resources] of this._annotationResources) {
            resources.hotspotDom?.classList.remove('is-expanded');
            this._hideCalloutLeader(resources);
            if (resources.expandedTooltip) {
                this._stopTooltipMedia(resources.expandedTooltip.attachmentsDom);
                resources.expandedTooltip.tooltipDom.style.opacity = '0';
                resources.expandedTooltip.tooltipDom.style.visibility = 'hidden';
            }
        }
    }

    _getTooltipReferenceDistance(annotation, currentDistance) {
        const position = annotation?.cameraPose?.position;
        const annotationPosition = annotation?.entity?.getPosition?.();
        const values = [
            position?.x, position?.y, position?.z,
            annotationPosition?.x, annotationPosition?.y, annotationPosition?.z
        ].map(Number);
        if (values.every(Number.isFinite)) {
            const dx = values[0] - values[3];
            const dy = values[1] - values[4];
            const dz = values[2] - values[5];
            const referenceDistance = Math.hypot(dx, dy, dz);
            if (referenceDistance > 0.0001) return referenceDistance;
        }
        return Math.max(currentDistance, 0.0001);
    }

    _getTooltipScale(annotation) {
        const cameraPosition = this._camera?.getPosition?.();
        const annotationPosition = annotation?.entity?.getPosition?.();
        if (!cameraPosition || !annotationPosition) return 1;

        const currentDistance = Math.max(annotationPosition.distance(cameraPosition), 0.0001);
        let state = this._tooltipScaleStates.get(annotation);
        if (!state) {
            const referenceDistance = this._getTooltipReferenceDistance(annotation, currentDistance);
            state = { referenceDistance, scale: 1 };
            this._tooltipScaleStates.set(annotation, state);
        }

        const isMobile = (this.app?.graphicsDevice?.canvas?.clientWidth || window.innerWidth) <= 640;
        const range = isMobile ? mobileTooltipScaleRange : desktopTooltipScaleRange;
        const rawScale = Math.sqrt(state.referenceDistance / currentDistance);
        const targetScale = Math.min(range.max, Math.max(range.min, rawScale));
        const difference = targetScale - state.scale;
        if (Math.abs(difference) > tooltipScaleDeadband) {
            state.scale += difference * tooltipScaleSmoothing;
        }
        return state.scale;
    }

    _applyTooltipScale(tooltipDom, annotation) {
        if (!tooltipDom || !annotation) return 1;
        const scale = this._getTooltipScale(annotation);
        tooltipDom.style.setProperty('--pc-annotation-scale', scale.toFixed(3));
        return scale;
    }

    _showTooltipDom(tooltipDom) {
        if (!tooltipDom) return;
        const shouldAnimate = tooltipDom.style.visibility !== 'visible';
        tooltipDom.style.opacity = '1';
        tooltipDom.style.visibility = 'visible';
        tooltipDom.style.filter = 'none';
        tooltipDom.style.transform = 'scale(var(--pc-annotation-scale, 1))';
        if (
            shouldAnimate &&
            typeof tooltipDom.animate === 'function' &&
            !window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches
        ) {
            tooltipDom.getAnimations?.().forEach(animation => animation.cancel());
            tooltipDom.animate([
                {
                    opacity: 0,
                    filter: 'blur(4px)',
                    transform: 'translate3d(0, 10px, 0) scale(var(--pc-annotation-scale, 1))'
                },
                {
                    opacity: 1,
                    filter: 'blur(0)',
                    transform: 'translate3d(0, 0, 0) scale(var(--pc-annotation-scale, 1))'
                }
            ], {
                duration: 340,
                easing: 'cubic-bezier(.2,.82,.24,1)',
                fill: 'none'
            });
        }
    }

    _positionAnnotationPanel(resources, elements, left, top, parentBounds, showLeader = false) {
        const tooltipDom = elements?.tooltipDom;
        if (!tooltipDom || !resources) return;
        const leader = showLeader ? this._ensureCalloutLeader(resources) : null;

        const margin = 12;
        const gap = 28;
        const rightSpace = parentBounds.width - margin - left - gap;
        const leftSpace = left - gap - margin;
        const centerX = parentBounds.width / 2;
        const sideHysteresis = Math.max(42, Math.min(72, parentBounds.width * 0.1));
        let direction = resources.calloutDirection === -1 || resources.calloutDirection === 1
            ? resources.calloutDirection
            : (left <= centerX ? 1 : -1);
        if (
            direction > 0 && (
                left > centerX + sideHysteresis ||
                (rightSpace < 104 && leftSpace > rightSpace + 56)
            )
        ) {
            direction = -1;
        } else if (
            direction < 0 && (
                left < centerX - sideHysteresis ||
                (leftSpace < 104 && rightSpace > leftSpace + 56)
            )
        ) {
            direction = 1;
        }
        resources.calloutDirection = direction;

        const scale = Number.parseFloat(
            tooltipDom.style.getPropertyValue('--pc-annotation-scale')
        ) || 1;
        const availableWidth = Math.max(96, direction > 0 ? rightSpace : leftSpace);
        const panelMaxWidth = Math.min(164, availableWidth / scale);
        tooltipDom.style.width = 'max-content';
        tooltipDom.style.minWidth = `${Math.min(112, panelMaxWidth)}px`;
        tooltipDom.style.maxWidth = `${panelMaxWidth}px`;
        tooltipDom.style.maxHeight = `${Math.max(74, Math.min(172, (parentBounds.height - margin * 2) / scale))}px`;

        const tooltipWidth = (tooltipDom.offsetWidth || panelMaxWidth) * scale;
        const tooltipHeight = (tooltipDom.offsetHeight || 80) * scale;
        const maxTop = Math.max(margin, parentBounds.height - tooltipHeight - margin);
        const desiredAnchorY = top - 18;
        const panelTop = Math.min(
            Math.max(desiredAnchorY - Math.min(24, tooltipHeight / 2), margin),
            maxTop
        );
        const preferredPanelLeft = direction > 0
            ? Math.min(left + gap, parentBounds.width - tooltipWidth - margin)
            : Math.max(margin, left - gap - tooltipWidth);
        const panelLeft = Math.min(
            Math.max(preferredPanelLeft, margin),
            Math.max(margin, parentBounds.width - tooltipWidth - margin)
        );
        const panelEdgeX = direction > 0 ? panelLeft : panelLeft + tooltipWidth;
        const panelEdgeY = Math.min(
            Math.max(desiredAnchorY, panelTop + 12),
            panelTop + Math.max(12, tooltipHeight - 12)
        );
        const bendX = left + direction * 18;

        tooltipDom.style.left = `${panelLeft}px`;
        tooltipDom.style.top = `${panelTop}px`;

        if (leader) {
            leader.svg.setAttribute('viewBox', `0 0 ${parentBounds.width} ${parentBounds.height}`);
            leader.svg.style.width = `${parentBounds.width}px`;
            leader.svg.style.height = `${parentBounds.height}px`;
            leader.path.setAttribute(
                'd',
                `M ${left.toFixed(2)} ${top.toFixed(2)} L ${bendX.toFixed(2)} ${panelEdgeY.toFixed(2)} L ${panelEdgeX.toFixed(2)} ${panelEdgeY.toFixed(2)}`
            );
            leader.svg.style.visibility = 'visible';
            leader.svg.style.opacity = '1';
            leader.svg.classList.add('is-visible');
        } else {
            this._hideCalloutLeader(resources);
        }
    }

    /**
     * Hides all elements when annotation is behind camera.
     * @param {Annotation} annotation - The annotation
     * @param {AnnotationResources} resources - The annotation's resources
     * @private
     */
    _hideAnnotationElements(annotation, resources) {
        resources.hotspotDom.style.display = 'none';
        this._hideCalloutLeader(resources);
        if (resources.expandedTooltip) {
            this._stopTooltipMedia(resources.expandedTooltip.attachmentsDom);
            resources.expandedTooltip.tooltipDom.style.opacity = '0';
            resources.expandedTooltip.tooltipDom.style.visibility = 'hidden';
        }
        if (this._activeAnnotation === annotation) {
            if (this._tooltipDom.style.visibility !== 'hidden') {
                this._hideTooltip(annotation);
            }
        }
    }

    /**
     * Updates screen-space positions of HTML elements.
     * @param {Annotation} annotation - The annotation
     * @param {AnnotationResources} resources - The annotation's resources
     * @param {Vec3} screenPos - Screen coordinate
     * @private
     */
    _updateAnnotationPositions(annotation, resources, screenPos) {
        const canvas = this.app.graphicsDevice.canvas;
        const parentBounds = this._getParentBounds();
        const canvasLeft = canvas.offsetParent === this._parentDom ? canvas.offsetLeft : 0;
        const canvasTop = canvas.offsetParent === this._parentDom ? canvas.offsetTop : 0;
        const left = screenPos.x + canvasLeft;
        const top = screenPos.y + canvasTop;

        if (
            left < parentBounds.left ||
            top < parentBounds.top ||
            left > parentBounds.right ||
            top > parentBounds.bottom
        ) {
            this._hideAnnotationElements(annotation, resources);
            return;
        }

        resources.hotspotDom.style.display = 'block';
        resources.hotspotDom.style.left = `${left}px`;
        resources.hotspotDom.style.top = `${top}px`;

        if (this._allTooltipsVisible && annotation.label !== '0') {
            const elements = this._ensureExpandedTooltip(annotation, resources);
            this._applyTooltipScale(elements.tooltipDom, annotation);
            this._showTooltipDom(elements.tooltipDom);
            this._positionAnnotationPanel(
                resources,
                elements,
                left,
                top,
                parentBounds,
                this._calloutMode
            );
        }

        if (this._activeAnnotation === annotation) {
            this._applyTooltipScale(this._tooltipDom, annotation);
            this._positionAnnotationPanel(
                resources,
                { tooltipDom: this._tooltipDom },
                left,
                top,
                parentBounds,
                this._calloutMode
            );
        }
    }

    /**
     * Updates 3D rotation and scale of hotspot planes.
     * @param {Annotation} annotation - The annotation
     * @private
     */
    _updateAnnotationRotationAndScale(annotation) {
        const cameraRotation = this._camera.getRotation();
        annotation.entity.setRotation(cameraRotation);
        annotation.entity.rotateLocal(90, 0, 0);

        const cameraPos = this._camera.getPosition();
        const distance = annotation.entity.getPosition().distance(cameraPos);
        const canvas = this.app.graphicsDevice.canvas;
        const screenHeight = canvas.clientHeight;
        const projMatrix = this._camera.camera.projectionMatrix;
        const worldSize = (this._hotspotSize / screenHeight) * (2 * distance / projMatrix.data[5]);
        const scale = this._getAnnotationSizeScale(annotation);

        annotation.entity.setLocalScale(worldSize * scale, worldSize * scale, worldSize * scale);
    }

    /**
     * Handles label change for an annotation.
     * @param {Annotation} annotation - The annotation
     * @param {string} label - The new label
     * @private
     */
    _onLabelChange(annotation, label) {
        const resources = this._annotationResources.get(annotation);
        if (!resources) return;
        this._replaceAnnotationTexture(annotation, resources);
    }

    /**
     * Handles title change for an annotation.
     * @param {Annotation} annotation - The annotation
     * @param {string} title - The new title
     * @private
     */
    _onTitleChange(annotation, title) {
        if (this._activeAnnotation === annotation) {
            this._titleDom.textContent = title;
        }
        const resources = this._annotationResources.get(annotation);
        if (resources?.expandedTooltip) {
            resources.expandedTooltip.titleDom.textContent = title;
        }
    }

    /**
     * Handles text change for an annotation.
     * @param {Annotation} annotation - The annotation
     * @param {string} text - The new text
     * @private
     */
    _onTextChange(annotation, text) {
        if (this._activeAnnotation === annotation) {
            this._textDom.textContent = text;
        }
        const resources = this._annotationResources.get(annotation);
        if (resources?.expandedTooltip) {
            resources.expandedTooltip.textDom.textContent = text;
        }
    }

    _onTextStyleChange(annotation) {
        if (this._activeAnnotation === annotation) {
            this._applyAnnotationTextStyle(annotation);
        }
        const resources = this._annotationResources.get(annotation);
        if (resources?.expandedTooltip) {
            this._applyAnnotationTextStyle(annotation, resources.expandedTooltip);
        }
    }

    _onAttachmentsChange(annotation) {
        const resources = this._annotationResources.get(annotation);
        if (!resources) return;

        if (!resources.tooltipAttachmentsDom) {
            resources.tooltipAttachmentsDom = this._createAttachmentsDom();
        }
        this._renderAttachmentsInto(annotation, resources.tooltipAttachmentsDom);

        if (this._activeAnnotation === annotation) {
            this._renderAttachments(annotation);
        } else if (this._allTooltipsVisible && resources.expandedTooltip) {
            this._ensureExpandedTooltip(annotation, resources);
        }
    }

    /**
     * Handles enable event for an annotation.
     * @param {Annotation} annotation - The annotation
     * @private
     */
    _onAnnotationEnable(annotation) {
        const resources = this._annotationResources.get(annotation);
        if (!resources) return;

        resources.baseEntity.enabled = true;
        resources.overlayEntity.enabled = true;
        resources.hotspotDom.style.display = '';
    }

    /**
     * Handles disable event for an annotation.
     * @param {Annotation} annotation - The annotation
     * @private
     */
    _onAnnotationDisable(annotation) {
        const resources = this._annotationResources.get(annotation);
        if (!resources) return;

        resources.baseEntity.enabled = false;
        resources.overlayEntity.enabled = false;
        resources.hotspotDom.style.display = 'none';
        resources.hotspotDom.classList.remove('is-active', 'is-expanded');
        this._hideCalloutLeader(resources);
        if (resources.expandedTooltip) {
            resources.expandedTooltip.tooltipDom.style.opacity = '0';
            resources.expandedTooltip.tooltipDom.style.visibility = 'hidden';
        }

        if (this._activeAnnotation === annotation) {
            this._hideTooltip(annotation);
        }
        if (this._hoverAnnotation === annotation) {
            this._hoverAnnotation = null;
            this._setAnnotationHover(annotation, false);
        }
    }

    /**
     * Registers an annotation and creates its rendering resources.
     * Called automatically when an `annotation:add` event is received.
     * @param {Annotation} annotation - The annotation to register
     * @private
     */
    _registerAnnotation(annotation) {
        if (this._annotationResources.has(annotation)) {
            return;
        }

        const eventHandles = [];

        // Create texture
        const texture = this._createAnnotationTexture(annotation);

        // Create materials
        const materials = [
            this._createHotspotMaterial(texture, {
                opacity: 1,
                depthTest: true,
                depthWrite: true
            }),
            this._createHotspotMaterial(texture, {
                opacity: this._behindOpacity,
                depthTest: false,
                depthWrite: false
            })
        ];

        // Create base entity
        const baseEntity = new Entity('base', this.app);
        const baseMi = new MeshInstance(this._mesh, materials[0]);
        baseMi.cull = false;
        baseEntity.addComponent('render', {
            layers: [this._layers[0].id],
            meshInstances: [baseMi]
        });

        // Create overlay entity
        const overlayEntity = new Entity('overlay', this.app);
        const overlayMi = new MeshInstance(this._mesh, materials[1]);
        overlayMi.cull = false;
        overlayEntity.addComponent('render', {
            layers: [this._layers[1].id],
            meshInstances: [overlayMi]
        });

        annotation.entity.addChild(baseEntity);
        annotation.entity.addChild(overlayEntity);

        // Create hotspot DOM
        const hotspotDom = document.createElement('div');
        hotspotDom.className = 'pc-annotation-hotspot';

        // Click handler
        const onHotspotClick = (e) => {
            if (annotation?.label === '0') {
                e.stopPropagation();
                return;
            }
            e.stopPropagation();
            this._activateAnnotation(annotation, e);
        };
        hotspotDom.addEventListener('click', onHotspotClick);

        // Hover handlers
        const onPointerEnter = () => {
            if (annotation?.label === '0') return;
            if(this._annotationEditMode) return;
            if (this._hoverAnnotation !== null) {
                this._setAnnotationHover(this._hoverAnnotation, false);
            }
            this._hoverAnnotation = annotation;
            this._setAnnotationHover(annotation, true);
        };

        const onPointerLeave = () => {
            if (annotation?.label === '0') return;
            if(this._annotationEditMode) return;
            if (this._hoverAnnotation === annotation) {
                this._hoverAnnotation = null;
                this._setAnnotationHover(annotation, false);
            }
        };

        hotspotDom.addEventListener('pointerenter', onPointerEnter);
        hotspotDom.addEventListener('pointerleave', onPointerLeave);

        // Wheel passthrough
        const onWheel = (e) => {
            const canvas = this.app.graphicsDevice.canvas;
            canvas.dispatchEvent(new WheelEvent(e.type, e));
        };
        hotspotDom.addEventListener('wheel', onWheel);

        this._parentDom.appendChild(hotspotDom);

        this._applyAnnotationHotspotSize(annotation);

        // Listen for annotation attribute changes
        eventHandles.push(annotation.on('label:set', label => this._onLabelChange(annotation, label)));
        eventHandles.push(annotation.on('title:set', title => this._onTitleChange(annotation, title)));
        eventHandles.push(annotation.on('text:set', text => this._onTextChange(annotation, text)));
        eventHandles.push(annotation.on('titleColor:set', () => this._onTextStyleChange(annotation)));
        eventHandles.push(annotation.on('textColor:set', () => this._onTextStyleChange(annotation)));
        eventHandles.push(annotation.on('textSize:set', () => this._onTextStyleChange(annotation)));
        eventHandles.push(annotation.on('attachments:set', () => this._onAttachmentsChange(annotation)));
        eventHandles.push(annotation.on('size:set', () => {
            this._applyAnnotationHotspotSize(annotation);
            this._updateAnnotationRotationAndScale(annotation);
        }));
        eventHandles.push(annotation.on('enable', () => this._onAnnotationEnable(annotation)));
        eventHandles.push(annotation.on('disable', () => this._onAnnotationDisable(annotation)));

        // Store cleanup function for DOM listeners
        const domCleanup = () => {
            hotspotDom.removeEventListener('click', onHotspotClick);
            hotspotDom.removeEventListener('pointerenter', onPointerEnter);
            hotspotDom.removeEventListener('pointerleave', onPointerLeave);
            hotspotDom.removeEventListener('wheel', onWheel);
        };

        // Store resources
        /** @type {AnnotationResources} */
        const resources = {
            baseEntity,
            overlayEntity,
            hotspotDom,
            texture,
            materials,
            eventHandles,
            domCleanup,
            calloutLeader: null,
            calloutDirection: 0,
            expandedTooltip: null,
            tooltipAttachmentsDom: null
        };

        this._annotationResources.set(annotation, resources);
    }

    /**
     * Unregisters an annotation and destroys its rendering resources.
     * Called automatically when an `annotation:remove` event is received.
     * @param {Annotation} annotation - The annotation to unregister
     * @private
     */
    _unregisterAnnotation(annotation) {
        const resources = this._annotationResources.get(annotation);
        if (!resources) return;

        if (this._pendingTooltipAnnotation === annotation) {
            this._clearPendingTooltip();
        }

        // Clear active/hover state
        if (this._activeAnnotation === annotation) {
            this._activeAnnotation = null;
            this._tooltipDom.style.visibility = 'hidden';
        }
        if (this._hoverAnnotation === annotation) {
            this._hoverAnnotation = null;
        }

        // Unbind event handles
        resources.eventHandles.forEach(handle => handle.off());
        resources.eventHandles.length = 0;

        // Remove DOM listeners
        resources.domCleanup();

        // Remove DOM element
        resources.hotspotDom.remove();
        resources.calloutLeader?.svg?.remove();
        if (resources.tooltipAttachmentsDom === this._attachmentsDom) {
            this._stopTooltipMedia(this._attachmentsDom);
            const emptyAttachmentsDom = this._createAttachmentsDom();
            if (this._attachmentsDom?.parentNode === this._tooltipDom) {
                this._attachmentsDom.replaceWith(emptyAttachmentsDom);
            }
            this._attachmentsDom = emptyAttachmentsDom;
        }
        resources.tooltipAttachmentsDom?.remove();
        resources.tooltipAttachmentsDom = null;
        resources.expandedTooltip?.tooltipDom?.remove();

        // Destroy entities
        resources.baseEntity.destroy();
        resources.overlayEntity.destroy();

        // Destroy materials
        resources.materials.forEach(mat => mat.destroy());
        resources.materials.length = 0;

        // Destroy texture
        resources.texture.destroy();

        this._annotationResources.delete(annotation);
    }

    initialize() {
        // Set parent DOM
        if (this._parentDom === null) {
            this._parentDom = this._resolveParentDom();
        }

        // Inject styles
        this._injectStyles();

        // Create layers
        const { layers } = this.app.scene;
        const worldLayer = layers.getLayerByName('World');

        const createLayer = (name, semitrans) => {
            const layer = new Layer({ name });
            const idx = semitrans ? layers.getTransparentIndex(worldLayer) : layers.getOpaqueIndex(worldLayer);
            layers.insert(layer, idx + 1);
            return layer;
        };

        this._layers = [
            createLayer('HotspotBase', false),
            createLayer('HotspotOverlay', true)
        ];

        // Find camera if not set
        if (this._camera === null) {
            const cameraComponent = this.app.root.findComponent('camera');
            if (cameraComponent) {
                this._camera = cameraComponent.entity;
            }
        }

        // Add layers to camera
        if (this._camera) {
            this._camera.camera.layers = [
                ...this._camera.camera.layers,
                ...this._layers.map(layer => layer.id)
            ];
        }

        // Create shared mesh
        this._mesh = Mesh.fromGeometry(this.app.graphicsDevice, new PlaneGeometry({
            widthSegments: 1,
            lengthSegments: 1
        }));
        // Keep one manager-owned reference alive. RenderComponent destroys its
        // MeshInstances when an annotation entity is removed; without this
        // reference, removing the final annotation also destroys the shared
        // mesh before discard/restore can create replacement annotations.
        this._mesh.incRefCount();

        // Initialize tooltip DOM
        this._tooltipDom = document.createElement('div');
        this._tooltipDom.className = 'pc-annotation';

        this._titleDom = document.createElement('div');
        this._titleDom.className = 'pc-annotation-title';
        this._tooltipDom.appendChild(this._titleDom);

        this._textDom = document.createElement('div');
        this._textDom.className = 'pc-annotation-text';
        this._tooltipDom.appendChild(this._textDom);

        this._attachmentsDom = this._createAttachmentsDom();
        this._tooltipDom.appendChild(this._attachmentsDom);

        this._parentDom.appendChild(this._tooltipDom);
        this._tooltipDom.addEventListener('pointerdown', event => event.stopPropagation());

        // Single document-level listener to dismiss active tooltip
        const onDocumentPointerDown = (event) => {
            if (this._annotationEditMode) return;

            // Let the hotspot click handler decide whether to open or close
            // the tooltip. Handling this here first would clear the active
            // annotation before _activateAnnotation can perform the toggle.
            const target = event?.target;
            const isHotspotPointerDown = target && Array.from(this._annotationResources.values())
                .some(resources => resources.hotspotDom === target || resources.hotspotDom.contains?.(target));
            if (isHotspotPointerDown) return;

            if (this._activeAnnotation) {
                this._hideTooltip(this._activeAnnotation);
            } else {
                this._clearPendingTooltip();
            }
        };
        document.addEventListener('pointerdown', onDocumentPointerDown);

        const onEditModeChange = (enabled) => {
            this._annotationEditMode = enabled;
            if (enabled && this._allTooltipsVisible) {
                this.hideAllTooltips();
            }
            if (enabled && this._activeAnnotation) {
                this._hideTooltip(this._activeAnnotation);
            }
        };
        this.app.on('annotation:editMode', onEditModeChange);

        // Listen for annotation add/remove events on the app
        const onAnnotationAdd = annotation => this._registerAnnotation(annotation);
        const onAnnotationRemove = annotation => this._unregisterAnnotation(annotation);

        this.app.on('annotation:add', onAnnotationAdd);
        this.app.on('annotation:remove', onAnnotationRemove);

        // Prerender handler - update all annotations
        const prerenderHandler = () => {
            if (!this._camera) return;

            for (const [annotation, resources] of this._annotationResources) {
                if (!annotation.enabled) {
                    this._hideAnnotationElements(annotation, resources);
                    continue;
                }

                const position = annotation.entity.getPosition();
                const canvas = this.app.graphicsDevice.canvas;
                const width = canvas.clientWidth || this.app.graphicsDevice.clientRect.width;
                const height = canvas.clientHeight || this.app.graphicsDevice.clientRect.height;
                // CameraComponent.worldToScreen uses getBoundingClientRect(), whose
                // width and height are swapped after the viewer is CSS-rotated.
                // Project with the canvas' layout dimensions so hotspot DOM and
                // the rendered marker stay on the same point in software landscape.
                const camera = this._camera.camera;
                const screenPos = camera.camera?.worldToScreen
                    ? camera.camera.worldToScreen(position, width, height)
                    : camera.worldToScreen(position);

                const { viewMatrix } = this._camera.camera;
                viewMatrix.transformPoint(position, vec);
                if (vec.z >= 0) {
                    this._hideAnnotationElements(annotation, resources);
                    continue;
                }

                this._updateAnnotationPositions(annotation, resources, screenPos);
                this._updateAnnotationRotationAndScale(annotation);

                // Update material opacity
                resources.materials[0].opacity = this._opacity;
                resources.materials[1].opacity = this._behindOpacity * this._opacity;
                resources.materials[0].setParameter('material_opacity', this._opacity);
                resources.materials[1].setParameter('material_opacity', this._behindOpacity * this._opacity);
            }
        };
        this.app.on('prerender', prerenderHandler);

        // Clean up on destroy
        this.once('destroy', () => {
            this._clearPendingTooltip();

            // Unregister all annotations
            for (const annotation of this._annotationResources.keys()) {
                this._unregisterAnnotation(annotation);
            }

            // Remove event listeners
            this.app.off('annotation:add', onAnnotationAdd);
            this.app.off('annotation:remove', onAnnotationRemove);
            this.app.off('prerender', prerenderHandler);
            this.app.off('annotation:editMode', onEditModeChange);
            document.removeEventListener('pointerdown', onDocumentPointerDown);

            // Remove DOM elements
            if (this._tooltipDom) {
                this._tooltipDom.remove();
                this._tooltipDom = null;
            }
            if (this._styleSheet) {
                this._styleSheet.remove();
                this._styleSheet = null;
            }

            // Remove layers from camera
            if (this._camera && this._camera.camera) {
                const layerIds = this._layers.map(layer => layer.id);
                this._camera.camera.layers = this._camera.camera.layers.filter(
                    id => !layerIds.includes(id)
                );
            }

            // Remove layers from scene
            const { layers } = this.app.scene;
            this._layers.forEach(layer => layers.remove(layer));
            this._layers = [];

            // Destroy mesh
            if (this._mesh) {
                this._mesh.decRefCount();
                if (this._mesh.refCount < 1) {
                    this._mesh.destroy();
                }
                this._mesh = null;
            }
        });
    }
}

/**
 * A lightweight data script for creating interactive 3D annotations in a scene.
 * This script only holds the annotation data (label, title, text) - all rendering
 * and interaction is handled by an AnnotationManager listening for app events.
 *
 * Fires the following app-level events:
 * - `annotation:add` - when the annotation initializes
 * - `annotation:remove` - when the annotation is destroyed
 *
 * Fires the following script-level events (listened to by AnnotationManager):
 * - `label:set` - when label changes
 * - `title:set` - when title changes
 * - `text:set` - when text changes
 * - `titleColor:set` - when tooltip title color changes
 * - `textColor:set` - when tooltip body text color changes
 * - `textSize:set` - when tooltip body text size changes
 * - `hover` - when hover state changes
 * - `show` - when tooltip is shown
 * - `hide` - when tooltip is hidden
 */
export class Annotation extends Script {
    static scriptName = 'annotation';

    /** @private */
    _label = '';

    /** @private */
    _title = '';

    /** @private */
    _text = '';

    /** @private */
    _titleColor = '#ffffff';

    /** @private */
    _textColor = '#ffffff';

    /** @private */
    _textSize = 14;

    /** @private */
    _size = 1;

    /** @private */
    _cameraPose = null;

    /** @private */
    _attachments = [];

    /**
     * The short text displayed on the hotspot circle (e.g. "1", "A").
     *
     * @attribute
     * @title Label
     * @type {string}
     */
    set label(value) {
        this._label = value;
        this.fire('label:set', value);
    }

    get label() {
        return this._label;
    }

    /**
     * The title shown in the tooltip when the hotspot is clicked.
     *
     * @attribute
     * @title Title
     * @type {string}
     */
    set title(value) {
        this._title = value;
        this.fire('title:set', value);
    }

    get title() {
        return this._title;
    }

    /**
     * The description text shown in the tooltip when the hotspot is clicked.
     *
     * @attribute
     * @title Text
     * @type {string}
     */
    set text(value) {
        this._text = value;
        this.fire('text:set', value);
    }

    get text() {
        return this._text;
    }

    /**
     * The color used for tooltip title text.
     *
     * @attribute
     * @title Title Color
     * @type {string}
     * @default "#ffffff"
     */
    set titleColor(value) {
        const next = String(value || '').trim();
        this._titleColor = /^#[0-9a-f]{6}$/i.test(next) ? next : '#ffffff';
        this.fire('titleColor:set', this._titleColor);
    }

    get titleColor() {
        return this._titleColor;
    }

    /**
     * The color used for tooltip description text.
     *
     * @attribute
     * @title Text Color
     * @type {string}
     * @default "#ffffff"
     */
    set textColor(value) {
        const next = String(value || '').trim();
        this._textColor = /^#[0-9a-f]{6}$/i.test(next) ? next : '#ffffff';
        this.fire('textColor:set', this._textColor);
    }

    get textColor() {
        return this._textColor;
    }

    /**
     * Tooltip text size in CSS pixels.
     *
     * @attribute
     * @title Text Size
     * @type {number}
     * @default 14
     */
    set textSize(value) {
        const next = Number(value);
        this._textSize = Number.isFinite(next)
            ? Math.min(32, Math.max(10, Math.round(next)))
            : 14;
        this.fire('textSize:set', this._textSize);
    }

    get textSize() {
        return this._textSize;
    }

    /**
     * Media files attached to this annotation.
     *
     * @attribute
     * @title Attachments
     * @type {object[]}
     */
    set attachments(value) {
        this._attachments = Array.isArray(value) ? value : [];
        this.fire('attachments:set', this._attachments);
    }

    get attachments() {
        return this._attachments;
    }

    /**
     * Size multiplier for this hotspot (1 = default size).
     * @attribute
     * @title Size
     * @type {number}
     * @default 1
     */
    set size(value) {
        const next = Number(value);
        this._size = Number.isFinite(next) && next > 0 ? next : 1;
        this.fire('size:set', this._size);
    }

    get size() {
        return this._size;
    }

    /**
     * Camera pose snapshot for this annotation.
     * shape: { position: {x,y,z}, focus: {x,y,z} }
     *
     * @attribute
     * @title Camera Pose
     * @type {object}
     */
    set cameraPose(value) {
        const pos = value?.position;
        const focus = value?.focus;
        const valid = [
            pos?.x, pos?.y, pos?.z,
            focus?.x, focus?.y, focus?.z
        ].every(n => Number.isFinite(Number(n)));

        this._cameraPose = valid ? {
            position: {
                x: Number(pos.x),
                y: Number(pos.y),
                z: Number(pos.z)
            },
            focus: {
                x: Number(focus.x),
                y: Number(focus.y),
                z: Number(focus.z)
            }
        } : null;
        this.fire('cameraPose:set', this._cameraPose);
    }

    get cameraPose() {
        return this._cameraPose;
    }

    /**
     * Called after all scripts have initialized, ensuring the AnnotationManager
     * is ready to receive the annotation:add event.
     */
    postInitialize() {
        // Notify any listeners that this annotation has been created
        this.app.fire('annotation:add', this);

        // Clean up on destroy
        this.once('destroy', () => {
            this.app.fire('annotation:remove', this);
        });
    }
}
